import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type PropsWithChildren, useState } from 'react';
import { type DropEvent, type FileRejection, useDropzone } from 'react-dropzone';

import { uploadAudienceFile } from '@/api/audience';
import UploadIcon from '@/assets/svg/cloudUpload.svg?react';
import FileIcon from '@/assets/svg/file.svg?react';
import TrashIcon from '@/assets/svg/trashcan.svg?react';
import { Button } from '@/components/buttons';
import {
  Progress,
  ProgressIndicator,
  ProgressLabel,
  ProgressTrack,
  ProgressValue,
} from '@/components/Progress';
import { Link, SupportLink, Text } from '@/components/typography';
import SolAPIError from '@/error/SolAPIError';
import { numberFormatBytes } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';
import { useNotification } from '@/providers/Notification';

enum VIEW_STATE {
  DEFAULT,
  UPLOADING,
  UPLOADED,
  ERROR,
}

type Props = {
  'aria-invalid'?: boolean;
  disabled?: boolean;
  maxFileSize?: number;
  onChange?: (value: string | null) => void;
  onBlur?: () => void;
  onFileAccepted?: (file: File, event: DropEvent) => void;
  onFileRejected?: (fileRejections: FileRejection[], event: DropEvent) => void;
  onUploadError?: (errorMessage: string) => void;
};

const AudienceUpload = ({
  children,
  'aria-invalid': ariaInvalid,
  disabled = false,
  maxFileSize,
  onChange,
  onBlur,
  onFileAccepted,
  onFileRejected,
  onUploadError,
  ...props
}: PropsWithChildren<Props>) => {
  const { pushNotification } = useNotification();
  const [viewState, setViewState] = useState<VIEW_STATE>(VIEW_STATE.DEFAULT);

  const {
    getRootProps,
    getInputProps,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
    acceptedFiles,
  } = useDropzone({
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    disabled,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    minSize: 0,
    maxSize: maxFileSize,
    onDropRejected: (fileRejections, event) => {
      if (onFileRejected) {
        onFileRejected(fileRejections, event);
      }
    },
    onDropAccepted: async (files, event) => {
      const file = files[0];

      setViewState(VIEW_STATE.UPLOADING);

      if (onFileAccepted) {
        onFileAccepted(file, event);
      }

      try {
        const datasetId = await uploadAudienceFile(file);
        handleChange(datasetId);
        setViewState(VIEW_STATE.UPLOADED);
      } catch (e) {
        pushNotification({
          type: 'error',
          message: (
            <Text variant="caption1" weight="semi-bold" color="white">
              <Trans>
                Custom Audience failed to upload. Click{' '}
                <SupportLink
                  variant="caption1"
                  color="static-white"
                  path="CREATE_SALESFORCE_AUDIENCE"
                >
                  {t`HERE`}
                </SupportLink>{' '}
                for troubleshooting guidance or try uploading again.
              </Trans>
            </Text>
          ),
        });
        setViewState(VIEW_STATE.DEFAULT);
        handleChange(null);

        if (onUploadError && e instanceof SolAPIError) {
          onUploadError(e.getFirstError().message);
        }
      }
    },
  });

  const handleChange = (value: string | null) => {
    if (onChange) {
      onChange(value);
    }
  };

  const handleDeleteFile = () => {
    handleChange(null);
    setViewState(VIEW_STATE.DEFAULT);
  };

  return (
    <div {...props} onBlur={onBlur}>
      <input {...getInputProps()} />
      {viewState === VIEW_STATE.DEFAULT ? (
        <div
          aria-invalid={ariaInvalid || undefined}
          className={cn(
            'flex flex-1 flex-col items-center gap-y-2.5 rounded-sm border-2 border-dashed border-gray-600 bg-gray-100 p-5 text-center opacity-80',
            ariaInvalid && 'border-red-400 bg-red-400/20',
            (isFocused || isDragAccept) && 'border-green-400 bg-green-400/20',
            isDragReject && 'border-red-400 bg-red-400/20',
          )}
          {...getRootProps()}
        >
          <Text variant="body2">{children}</Text>
          <UploadIcon />
          <div>
            <Trans>
              or <br />
              <Link variant="body2" onClick={open}>{t`Browse Files`}</Link>
            </Trans>
          </div>
        </div>
      ) : (
        <div>
          {acceptedFiles.map((file) => (
            <div
              key={file.name}
              className="flex items-center gap-3 border border-dashed border-gray-600 px-3 py-6"
            >
              <FileIcon
                className={cn(
                  'text-gray-400',
                  viewState === VIEW_STATE.UPLOADED && 'text-green-400',
                )}
              />
              <div className="flex flex-1 flex-col gap-1">
                <Progress value={viewState === VIEW_STATE.UPLOADING ? null : 100}>
                  <ProgressLabel>{file.name}</ProgressLabel>
                  <ProgressValue />
                  <ProgressTrack>
                    <ProgressIndicator
                      className={cn(viewState === VIEW_STATE.UPLOADED && 'bg-green-400')}
                    />
                  </ProgressTrack>
                </Progress>
                <Text variant="caption2" color="grey">
                  <Trans>Filesize: {numberFormatBytes(file.size)}</Trans>
                </Text>
              </div>
              <Button
                variant="ghost"
                shape="square"
                danger
                disabled={disabled}
                size="icon"
                onClick={handleDeleteFile}
              >
                <TrashIcon />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AudienceUpload;
